import React, { useEffect } from 'react';

import {
	RadioButtonLocal as RadioButton,
	SliderCheckbox,
	SubmissionButtons,
} from '@components/common';

import { useQueryUser } from '@hooks/useQueryUser';

import { map } from '@store/selectors';
import { useAppDispatch, useAppSelector } from '@store/store';

import {
	applyShowAssetsLinkedToSites,
	resetAssetStateFilter,
	resetRightSideControls,
	setAppliedAcquiredAssetsTypeFilter,
	setApplyFiltersAvailableState,
	setCurrentAcquiredAssetsTypeFilter,
	setIsAcquiredAssetsShownApplied,
	setIsAcquiredAssetsShownCurrent,
	setShowAssetsLinkedToSitesState,
} from '@slices/map';

import { EAssetAcquiredStatuses, TAssetAcquiredStatuses } from '@enums/asset';
import { EOrganizationType } from '@enums/common';

import { acquiresAssetStatuses } from './constants';

import styles from './AssetsState.module.scss';

const AssetState = () => {
	const dispatch = useAppDispatch();
	const {
		appliedAcquiredAssetsTypeFilter,
		currentAcquiredAssetsTypeFilter,
		currentAssetsFilterValues: { isMyLinked: isMyLinkedLocalState },
		isAcquiredAssetsShownApplied,
		isAcquiredAssetsShownCurrent,
		isApplyFiltersAvailable,
		pinnedFilter,
	} = useAppSelector(map);

	const { organisationTypes } = useQueryUser();

	const isMNO = organisationTypes.some(type => type.name === EOrganizationType.networkOperator);
	const isAssetConsumer = organisationTypes.some(type => type.name === EOrganizationType.assetConsumer);

	const isAssetsLinkedToSitesShown = !isAssetConsumer || (isAssetConsumer && isMNO);

	const onChangeAssetsLinkedToSites = () => dispatch(setShowAssetsLinkedToSitesState(!isMyLinkedLocalState));

	const onChangeAcquiredAssetsShown = () => {
		dispatch(setIsAcquiredAssetsShownCurrent(!isAcquiredAssetsShownCurrent));
		dispatch(setShowAssetsLinkedToSitesState(false));
	};

	const onChangeAcquiredAssetsStatus = (status: TAssetAcquiredStatuses) => {
		dispatch(setCurrentAcquiredAssetsTypeFilter(status));
	};

	const handleApply = () => {
		dispatch(setAppliedAcquiredAssetsTypeFilter(currentAcquiredAssetsTypeFilter));
		dispatch(setIsAcquiredAssetsShownApplied(isAcquiredAssetsShownCurrent));
		dispatch(applyShowAssetsLinkedToSites(!!isMyLinkedLocalState));

		if (!pinnedFilter) dispatch(resetRightSideControls());
	};

	const handleReset = () => {
		dispatch(setCurrentAcquiredAssetsTypeFilter(EAssetAcquiredStatuses.all));
		dispatch(setIsAcquiredAssetsShownCurrent(false));
		dispatch(setShowAssetsLinkedToSitesState(false));
		dispatch(resetAssetStateFilter());

		if (!pinnedFilter) dispatch(resetRightSideControls());
	};

	useEffect(() => {
		if (isAcquiredAssetsShownCurrent !== isAcquiredAssetsShownApplied
			|| currentAcquiredAssetsTypeFilter !== appliedAcquiredAssetsTypeFilter
		) {
			dispatch(setApplyFiltersAvailableState(true));
		}
	}, [
		appliedAcquiredAssetsTypeFilter,
		currentAcquiredAssetsTypeFilter,
		dispatch,
		isAcquiredAssetsShownCurrent,
		isAcquiredAssetsShownApplied,
	]);

	return (
		<div className={styles.assetsStateContainer}>
			{isAssetsLinkedToSitesShown
				&& (
					<>
						<SliderCheckbox
							checked={!!isMyLinkedLocalState}
							disabled={!isAcquiredAssetsShownCurrent}
							id='assetsLinkedToSitesShown'
							isLabelAfter
							label='Show only assets with linked Telecom sites'
							onChange={onChangeAssetsLinkedToSites}
						/>
						<hr />
					</>
				)
			}
			<SliderCheckbox
				checked={isAcquiredAssetsShownCurrent}
				id='acquiredAssetsShown'
				isLabelAfter
				label='Show acquired assets by my organization'
				onChange={onChangeAcquiredAssetsShown}
			/>
			<div className={styles.sectionTitle}>Status</div>
			{acquiresAssetStatuses.map((status) => (
				<div
					key={status.value}
					className={styles.statusItem}
				>
					<RadioButton
						checked={currentAcquiredAssetsTypeFilter === status.value}
						disabled={!isAcquiredAssetsShownCurrent}
						id={status.value}
						infoTooltip={status.info}
						label={status.label}
						name='acquiredAssetsStatus'
						onChange={onChangeAcquiredAssetsStatus}
					/>
				</div>
			))}
			<SubmissionButtons
				disabledPositive={!isApplyFiltersAvailable}
				negativeHandler={handleReset}
				negativeLabel='Reset all'
				positiveHandler={handleApply}
				positiveLabel='Apply'
			/>
		</div>
	);
};

export default AssetState;
